import React from 'react';
import { motion } from 'framer-motion';
import { Battery, Shield, Gauge, CheckCircle,
        ArrowRight,
        isRTL } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';

const PowerDivisionCompany = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal via-slate to-charcoal">
      {/* Hero Section */}
      <section className="section-padding solid-bg text-white relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
          style={{
            backgroundImage: `url('/divisions/Power - Solar/electrical-substation-distribution-power-plant-wit-2024-07-25-01-00-57-utc.jpg')`
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/60 to-transparent" />
        
        {/* Floating Energy Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-3 h-3 bg-global-green rounded-full opacity-40 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 4}s`,
                animationDuration: `${4 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        <div className="container-max relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-12 items-center min-h-[60vh]">
            {/* Left Content */}
            <motion.div
              className="space-y-8"
              {...fadeInUp}
            >
              
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold t">
                Powering the Future with 
                <span className="text-white"> Trusted Partners</span>
              </h1>
              
              <p className="text-xl leading-relaxed opacity-90">
                Collaborating with industry-leading manufacturers to deliver cutting-edge power solutions. 
                From renewable energy systems to critical power infrastructure, we partner with companies 
                that share our commitment to reliability, innovation, and sustainable energy solutions.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" variant="secondary" className="group">
                    Get Quote
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/divisions/powercompany">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-charcoal">
                    Back to Power Division
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Companies Section */}
      <section className="section-padding bg-white text-black">
        <div className="container-max">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-global-green">
              Companies Global Technology Represent
            </h2>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Partnering with industry leaders to deliver world-class power solutions.
            </p>
          </motion.div>

          <motion.div
            className="bg-gradient-to-r from-charcoal to-slate rounded-2xl p-8 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4 flex items-center">
                  <Battery className="w-8 h-8 mr-3 text-global-green" />
                  <img src="/La_Marche.png" alt="La Marche Mfg. Co., USA" className="h-12 ml-2" />
                </h3>
                <h4 className="text-xl font-semibold mb-4 text-global-green">
                  Industry Leaders In AC/DC Solutions
                </h4>
                <h5 className="text-lg font-medium mb-4 text-black">
                  Ensure uninterrupted power supply round the clock 
                  using La Marche's durable Battery Chargers, UPS, and 
                  Power Conversion Solutions. Keep your vital systems 
                  operational and seamlessly running under any 
                  circumstances.
                </h5>
                <p className="text-black mb-6 leading-relaxed">
                  La Marche Manufacturing Company is a leading 
                  company in battery chargers and AC/DC power 
                  solutions, founded in 1945. With almost 80 years 
                  of experience, we serve various markets, including 
                  utilities, telecommunications, engine start systems, 
                  marine applications, uninterruptible power supplies 
                  (UPS), battery energy storage, railroads, solar power 
                  systems, and motive power. We design solutions to 
                  meet the specific needs of each industry, ensuring 
                  reliable and efficient power management that 
                  is crucial for uninterrupted operations. We are 
                  committed to quality by following ISO standards 
                  and conducting thorough quality control, along with 
                  substantial investment in research and development. 
                  This allows us to offer advanced products like high 
                  tech battery management systems and cutting-edge 
                  power conversion technologies.
                </p>
                <p className="text-black mb-6 leading-relaxed">
                  By combining in-house manufacturing with a 
                  focus on technology, La Marche remains a leader 
                  in the power solutions industry. Our dedication 
                  to innovation, quality, and outstanding customer 
                  service makes us a trusted partner worldwide. Our 
                  mission is to provide high-quality electrical power 
                  solutions that exceed customer expectations, driven 
                  by a commitment to predictive maintenance, 
                  sustainability, and teamwork.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-global-green mr-2" />
                    <span className="text-sm text-black">80+ Years Experience</span>
                  </div>
                  <div className="flex items-center">
                    <Gauge className="w-5 h-5 text-global-green mr-2" />
                    <span className="text-sm text-black">ISO Standards</span>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-black">Product Range</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center text-global-green">
                        <CheckCircle className="w-4 h-4 text-black mr-2 flex-shrink-0" />
                        Battery Chargers & UPS Systems
                      </li>
                      <li className="flex items-center text-global-green">
                        <CheckCircle className="w-4 h-4 text-black mr-2 flex-shrink-0" />
                        AC/DC Power Conversion Solutions
                      </li>
                      <li className="flex items-center text-global-green">
                        <CheckCircle className="w-4 h-4 text-black mr-2 flex-shrink-0" />
                        Battery Management Systems
                      </li>
                      <li className="flex items-center text-global-green">
                        <CheckCircle className="w-4 h-4 text-black mr-2 flex-shrink-0" />
                        Energy Storage Solutions
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-black">Applications</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center text-global-green">
                        <CheckCircle className="w-4 h-4 text-black mr-2 flex-shrink-0" />
                        Utilities & Telecommunications
                      </li>
                      <li className="flex items-center text-global-green">
                        <CheckCircle className="w-4 h-4 text-black mr-2 flex-shrink-0" />
                        Marine & Railroad Applications
                      </li>
                      <li className="flex items-center text-global-green">
                        <CheckCircle className="w-4 h-4 text-black mr-2 flex-shrink-0" />
                        Solar Power & Motive Power
                      </li>
                      <li className="flex items-center text-global-green">
                        <CheckCircle className="w-4 h-4 text-black mr-2 flex-shrink-0" />
                        Engine Start Systems
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="border-t border-white/20 pt-6">
                  <p className="text-sm leading-relaxed text-black opacity-90">
                    La Marche Manufacturing Company combines in-house manufacturing with cutting-edge technology to deliver reliable AC/DC power solutions. Each product is engineered to meet specific industry needs, ensuring uninterrupted operations and efficient power management.
                  </p>
                </div>

                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-lg font-semibold mb-2 text-black">Industry Markets</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Utilities</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Telecommunications</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Marine</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Railroad</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Solar Power</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">UPS Systems</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Battery Storage</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Motive Power</span>
                    <span className="bg-global-green/10 rounded px-2 py-1 text-black">Engine Start</span>
                  </div>
                </div>

                <div className="text-center pt-4">
                  <p className="text-sm font-medium text-black">
                    La Marche stands behind its products with industry-leading quality, innovation, and customer service
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PowerDivisionCompany;